import { useQuery } from "@apollo/client";
import { CompanyContainer, CompanyContentContainer } from "components/company/v3/CompanyContainers";
import getTabName from "components/company/valuation/getTabName";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import ValuationWallForTickerSymbolQuery from "lib/queries/ValuationWallForTickerSymbolQuery";
import type {
  IValuationWallForTickerSymbolQuery,
  IValuationWallForTickerSymbolQueryVariables,
} from "lib/queries/__generated__/ValuationWallForTickerSymbolQuery.generated";
import {
  CandlestickChartIcon,
  CircleDollarSignIcon,
  CircleHelpIcon,
  HandCoinsIcon,
  LineChartIcon,
  PercentIcon,
  RocketIcon,
  TriangleAlertIcon,
} from "lucide-react";
import Link from "next/link";
import { FormattedMessage, useIntl } from "react-intl";
import type { ISecurityCountry } from "types/graphqlTypes";
import { IValuationIndicatorCategory, IValuationIndicatorVerbiageLanguage } from "types/graphqlTypes";

interface ValuationBoxProps {
  tickerSymbol: string;
  securityCountry: ISecurityCountry;
}

const CATEGORY_ORDER = [
  IValuationIndicatorCategory.Valuation,
  IValuationIndicatorCategory.MarketCapitalization,
  IValuationIndicatorCategory.Earnings,
  IValuationIndicatorCategory.Profitability,
  IValuationIndicatorCategory.Growth,
  IValuationIndicatorCategory.Leverage,
];

const CATEGORY_ICONS = {
  [IValuationIndicatorCategory.Valuation]: CircleDollarSignIcon,
  [IValuationIndicatorCategory.MarketCapitalization]: CandlestickChartIcon,
  [IValuationIndicatorCategory.Earnings]: HandCoinsIcon,
  [IValuationIndicatorCategory.Profitability]: PercentIcon,
  [IValuationIndicatorCategory.Growth]: RocketIcon,
  [IValuationIndicatorCategory.Leverage]: TriangleAlertIcon,
};

const CATEGORY_ICON_STYLES = {
  [IValuationIndicatorCategory.Valuation]:
    "bg-indigo-50 text-indigo-600 dark:bg-indigo-300/10 dark:text-indigo-200",
  [IValuationIndicatorCategory.MarketCapitalization]:
    "bg-sky-50 text-sky-600 dark:bg-sky-300/10 dark:text-sky-200",
  [IValuationIndicatorCategory.Earnings]:
    "bg-emerald-50 text-emerald-600 dark:bg-emerald-300/10 dark:text-emerald-200",
  [IValuationIndicatorCategory.Profitability]:
    "bg-amber-50 text-amber-600 dark:bg-amber-300/10 dark:text-amber-200",
  [IValuationIndicatorCategory.Growth]:
    "bg-violet-50 text-violet-600 dark:bg-violet-300/10 dark:text-violet-200",
  [IValuationIndicatorCategory.Leverage]:
    "bg-rose-50 text-rose-600 dark:bg-rose-300/10 dark:text-rose-200",
};

export default function ValuationBox({ tickerSymbol, securityCountry }: ValuationBoxProps) {
  const intl = useIntl();
  const verbiageLanguage = intl.locale.startsWith("en")
    ? IValuationIndicatorVerbiageLanguage.EnUs
    : IValuationIndicatorVerbiageLanguage.PtBr;
  const { data, loading } = useQuery<IValuationWallForTickerSymbolQuery, IValuationWallForTickerSymbolQueryVariables>(
    ValuationWallForTickerSymbolQuery,
    {
      variables: {
        tickerSymbol,
        securityCountry,
        verbiageLanguage,
      },
    },
  );

  if (loading) {
    return (
      <CompanyContainer className="xl:h-fit">
        <CompanyContentContainer>
          <div className="grid w-full grid-cols-1 gap-2 p-4 sm:grid-cols-2 sm:p-5 lg:grid-cols-4">
            <LoadingSkeleton count={8} className="h-24" />
          </div>
        </CompanyContentContainer>
      </CompanyContainer>
    );
  }

  const indicators = data?.valuationWallForTickerSymbol ?? [];

  return (
    <CompanyContainer className="xl:h-fit">
      <CompanyContentContainer className="flex-col">
        <div className="w-full space-y-6 p-4 sm:p-5">
          {CATEGORY_ORDER.map((category) => {
            const categoryIndicators = indicators.filter((indicator) => indicator.category === category);

            if (categoryIndicators.length === 0) {
              return null;
            }

            const CategoryIcon = CATEGORY_ICONS[category];
            const categoryTitle = getTabName(intl, category);

            return (
              <section key={category} aria-labelledby={`ValuationCategory/${category}`} className="space-y-2.5">
                <Link
                  href={`/${securityCountry.toLowerCase()}/${tickerSymbol.toLowerCase()}/valuation?tab=${category}`}
                  className="group flex w-fit items-center gap-2.5 rounded-lg outline-none transition-opacity duration-150 ease-out hover:opacity-75 focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
                >
                  <div
                    className={classNames(
                      "flex size-7 items-center justify-center rounded-lg squircle",
                      CATEGORY_ICON_STYLES[category],
                    )}
                  >
                    <CategoryIcon size={15} strokeWidth={2} />
                  </div>
                  <div className="min-w-0">
                    <h2
                      id={`ValuationCategory/${category}`}
                      className="truncate font-aeonik text-base font-bold text-slate-700 dark:text-slate-200"
                    >
                      {categoryTitle}
                    </h2>
                  </div>
                </Link>

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {categoryIndicators.map((indicator) => (
                    <article
                      key={indicator.indicator}
                      className={classNames(
                        "flex flex-col gap-1.5 rounded-lg border border-slate-200/80 bg-slate-50/50 p-3",
                        "dark:border-slate-800 dark:bg-slate-800/40",
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="text-sm font-medium leading-5 text-slate-500 dark:text-slate-400">
                          {indicator.verbiage.abbreviation}
                        </h3>
                        <SimpleTextTooltip
                          content={
                            <div className="space-y-1.5">
                              <p>{indicator.verbiage.description}</p>
                              {indicator.verbiage.formula && (
                                <p className="font-mono text-xs text-slate-300">{indicator.verbiage.formula}</p>
                              )}
                            </div>
                          }
                        >
                          <button
                            type="button"
                            aria-label={indicator.verbiage.description}
                            className="shrink-0 rounded-full text-slate-400 outline-none transition-colors duration-150 ease-out hover:text-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:text-slate-500 dark:hover:text-indigo-300 dark:focus-visible:ring-offset-slate-800"
                          >
                            <CircleHelpIcon size={15} strokeWidth={2} />
                          </button>
                        </SimpleTextTooltip>
                      </div>

                      <div className="flex items-end justify-between gap-3">
                        <strong
                          className={classNames(
                            "min-w-0 truncate font-aeonik text-xl font-bold tabular-nums tracking-tight text-slate-800 dark:text-slate-100",
                            {
                              "text-red-600 dark:text-red-400": indicator.value < 0,
                              "text-slate-400 dark:text-slate-500": indicator.value === 0,
                            },
                          )}
                          title={indicator.formattedValue}
                        >
                          {indicator.formattedValue}
                        </strong>

                        {indicator.viewerCanSeeHistory && (
                          <SimpleTextTooltip content={categoryTitle}>
                            <Link
                              href={`/${securityCountry.toLowerCase()}/${tickerSymbol.toLowerCase()}/valuation/${indicator.indicator}?tab=${category}`}
                              aria-label={`${indicator.verbiage.abbreviation} — ${categoryTitle}`}
                              className={classNames(
                                "flex size-7 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500",
                                "transition-colors duration-150 ease-out hover:border-indigo-200 hover:text-indigo-600 active:scale-[0.97]",
                                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
                                "dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-indigo-500/50 dark:hover:text-indigo-300 dark:focus-visible:ring-offset-slate-800",
                              )}
                            >
                              <LineChartIcon size={15} strokeWidth={2} />
                            </Link>
                          </SimpleTextTooltip>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            );
          })}

          {indicators.length === 0 && (
            <div className="flex min-h-48 items-center justify-center text-center">
              <p className="text-base font-medium text-slate-500 dark:text-slate-400">
                <FormattedMessage defaultMessage="Nenhum indicador encontrado." id="BE9tbp" />
              </p>
            </div>
          )}
        </div>
      </CompanyContentContainer>
    </CompanyContainer>
  );
}
